import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { inWorkspace } from '../../config/opcoWorkspace'

// Writes the pointer position into --mx/--my so .collection-card's spotlight
// and border glow follow the cursor. Plain style writes, no React state, so
// sweeping across a grid never re-renders it.
export function trackSpotlight(e) {
  const el = e.currentTarget
  const rect = el.getBoundingClientRect()
  el.style.setProperty('--mx', `${e.clientX - rect.left}px`)
  el.style.setProperty('--my', `${e.clientY - rect.top}px`)
}

/**
 * Makes a whole card open `href` while keeping it a normal page: clicks on
 * buttons and links inside it do their own thing, selecting text doesn't
 * navigate, and cmd/ctrl/middle-click open a new tab. The card's title should
 * still be a real <Link> so keyboard and screen-reader users get a link.
 */
export function useCardLink(href) {
  const navigate = useNavigate()

  const open = useCallback((e) => {
    if (e.defaultPrevented || e.target.closest('a, button, input, select, textarea, [role="button"]')) return
    if (window.getSelection?.().toString()) return
    if (e.metaKey || e.ctrlKey || e.button === 1) {
      window.open(inWorkspace(href), '_blank')
      return
    }
    navigate(href)
  }, [href, navigate])

  const onAuxClick = useCallback((e) => {
    if (e.button === 1) open(e)
  }, [open])

  return { onClick: open, onAuxClick }
}

// Cursor-following glow for a list row. The glow is a composited layer moved
// with translate3d once per frame, so sweeping the list never repaints it.
export function useRowGlow() {
  const glowRef = useRef(null)
  const frame = useRef(0)
  useEffect(() => () => cancelAnimationFrame(frame.current), [])

  const place = (e, instant) => {
    const glow = glowRef.current
    if (!glow) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    cancelAnimationFrame(frame.current)
    if (instant) {
      // Entering the row: jump to the pointer rather than sweeping in from
      // wherever the glow was last left.
      glow.style.transition = 'none'
      glow.style.transform = `translate3d(${x}px, ${y}px, 0)`
      frame.current = requestAnimationFrame(() => { glow.style.transition = '' })
      return
    }
    frame.current = requestAnimationFrame(() => {
      glow.style.transform = `translate3d(${x}px, ${y}px, 0)`
    })
  }

  return {
    glowRef,
    onPointerEnter: e => place(e, true),
    onPointerMove: e => place(e, false),
  }
}
